import { Theme } from '@descope/page-editor-components';
import { Box } from '@mui/system';
import { isEmpty } from 'lodash';
import { withUpdateInPath } from 'pages/StyleEditor/helpers';
import React from 'react';
import Buttons from './Buttons';
import Containers from './Containers';
import Inputs from './Inputs';
import Logo from './Logo';
import { UpdateLogo } from './Logo/types';
import Section from './Section';
import Texts from './Texts';
import { UpdateColor, UpdateComponent } from './types';
import Typography from './Typography';
import { UpdateTypography } from './Typography/types';

type Props = {
	colors: Theme['colors'];
	updateColor: UpdateColor;
	components: Theme['components'];
	updateComponent: UpdateComponent;
	logo: Theme['logo'];
	updateLogo: UpdateLogo;
	typography: Theme['typography'];
	updateTypography: UpdateTypography;
};

const Controls: React.FC<
	Props &
		React.HTMLProps<HTMLDivElement> &
		Omit<React.ComponentProps<typeof Box>, keyof Props>
> = ({
	colors,
	updateColor,
	components,
	updateComponent,
	logo,
	updateLogo,
	typography,
	updateTypography,
	...props
}: Props) => (
	<Box {...props} overflow="scroll" height="100%">
		<Section title={intl.tr('styleEditor.logo.title')}>
			<Logo logo={logo} updateLogo={updateLogo} />
		</Section>

		<Section title={intl.tr('styleEditor.containers.title')}>
			<Containers
				backgroundColor={components?.['container-background-color']}
				updateBackgroundColor={withUpdateInPath(
					updateComponent,
					'container-background-color'
				)}
			/>
		</Section>

		<Section title={intl.tr('styleEditor.buttons.title')}>
			<Buttons
				primaryColor={colors?.primary}
				updatePrimaryColor={withUpdateInPath(updateColor, 'primary')}
				secondaryColor={colors?.secondary}
				updateSecondaryColor={withUpdateInPath(updateColor, 'secondary')}
				borderRadius={components?.['button-border-radius']}
				updateBorderRadius={withUpdateInPath(
					updateComponent,
					'button-border-radius'
				)}
			/>
		</Section>

		<Section title={intl.tr('styleEditor.inputs.title')}>
			<Inputs
				color={components?.['input-color']}
				updateColor={withUpdateInPath(updateComponent, 'input-color')}
				backgroundColor={components?.['input-background-color']}
				updateBackgroundColor={withUpdateInPath(
					updateComponent,
					'input-background-color'
				)}
				labelColor={components?.['input-label-color']}
				updateLabelColor={withUpdateInPath(
					updateComponent,
					'input-label-color'
				)}
				placeholderColor={components?.['input-placeholder-color']}
				updatePlaceholderColor={withUpdateInPath(
					updateComponent,
					'input-placeholder-color'
				)}
				borderRadius={components?.['input-border-radius']}
				updateBorderRadius={withUpdateInPath(
					updateComponent,
					'input-border-radius'
				)}
				borderOpacity={!components?.['input-border-opacity']}
				updateBorderOpacity={withUpdateInPath(
					updateComponent,
					'input-border-opacity'
				)}
			/>
		</Section>

		<Section title={intl.tr('styleEditor.texts.title')}>
			<Texts
				primary={components?.['primary-text-color']}
				updatePrimary={withUpdateInPath(updateComponent, 'primary-text-color')}
				secondary={components?.['secondary-text-color']}
				updateSecondary={withUpdateInPath(
					updateComponent,
					'secondary-text-color'
				)}
				link={colors?.accent}
				updateLink={withUpdateInPath(updateColor, 'accent')}
				error={colors?.error}
				updateError={withUpdateInPath(updateColor, 'error')}
			/>
		</Section>

		<Section title={intl.tr('styleEditor.typography.title')} disablePadding>
			{isEmpty(typography) ? null : (
				<Typography values={typography} updateTypography={updateTypography} />
			)}
		</Section>
	</Box>
);

export default Controls;
